import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, Check, Minus, Plus, Sparkles, ShieldCheck, Loader2, Lock, MessageSquare, Phone as PhoneIcon } from "lucide-react";
import { calculateEstimate, meshPriceForService, serviceLabel } from "@/lib/pricing";
import { trackGA } from "@/lib/analytics";
import { installTenantPixels, fireTenantLead, fireTenantPageView } from "@/lib/tenantPixels";
import { recordAttributionOnMount, getFirstAndLastTouch } from "@/lib/attribution";
import { toast } from "sonner";

/* Inline SVG icons — same shapes for rescreen vs new build, with a "NEW" badge on the new ones. */
function WindowScreenIcon({ isNew = false }) {
  return (
    <svg viewBox="0 0 40 40" className="w-9 h-9" aria-hidden="true">
      <defs>
        <pattern id="wmesh" width="2" height="2" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.45" fill="#475569" opacity="0.55" />
        </pattern>
      </defs>
      <rect x="5" y="3" width="30" height="34" rx="2" fill="#E2E8F0" stroke="#475569" strokeWidth="1.5" />
      <rect x="7.5" y="5.5" width="25" height="29" fill="url(#wmesh)" />
      <line x1="5" y1="20" x2="35" y2="20" stroke="#475569" strokeWidth="1.5" />
      {isNew && <NewBadge />}
    </svg>
  );
}

function SlidingDoorIcon({ isNew = false }) {
  return (
    <svg viewBox="0 0 40 40" className="w-9 h-9" aria-hidden="true">
      <defs>
        <pattern id="dmesh" width="2" height="2" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.45" fill="#475569" opacity="0.55" />
        </pattern>
      </defs>
      <rect x="10" y="2" width="20" height="36" rx="1.5" fill="#E2E8F0" stroke="#475569" strokeWidth="1.5" />
      <rect x="12" y="4" width="16" height="32" fill="url(#dmesh)" />
      <rect x="8" y="36" width="24" height="2" rx="0.5" fill="#94A3B8" />
      <rect x="26.5" y="18" width="1.6" height="6" rx="0.8" fill="#475569" />
      {isNew && <NewBadge />}
    </svg>
  );
}

function NewBadge() {
  // Diagonal NEW ribbon across the upper right corner of the 40x40 viewBox
  return (
    <g transform="translate(20 20) rotate(-30) translate(-20 -20)">
      <rect x="-4" y="6" width="48" height="7" fill="#F59E0B" />
      <text x="20" y="11.3" textAnchor="middle" fontSize="5.4" fontWeight="900" fill="#FFFFFF" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.6">NEW</text>
    </g>
  );
}

const SERVICE_META = {
  window_rescreen:       { label: "Window Screen Rescreen",  desc: "Replace mesh on existing window screen frames.", Icon: () => <WindowScreenIcon /> },
  new_window_screen:     { label: "New Window Screen",       desc: "We build a brand new screen for your window.",   Icon: () => <WindowScreenIcon isNew /> },
  sliding_door_rescreen: { label: "Sliding Door Rescreen",   desc: "Replace mesh on existing patio screen door.",    Icon: () => <SlidingDoorIcon /> },
  new_sliding_door:      { label: "New Sliding Door Screen", desc: "Built and installed from scratch.",              Icon: () => <SlidingDoorIcon isNew /> },
};

const STEPS_BY_MODE = {
  max: ["services", "quantity", "mesh", "contact"],
  high_intent: ["services", "quantity", "mesh", "summary", "contact"],
  hybrid: ["services", "quantity", "mesh", "range", "contact", "summary"],
};

// The platform funnel (/quote, no tenant slug) prepends a ZIP step so we can
// swap in the covering local company's pricing before the homeowner sees any prices.
const PLATFORM_ZIP_STEP = "zip";

// Compliance: exact SMS consent disclosure shown to homeowners on the funnel.
// Stored in the consent log so we can prove what each user opted in to.
const FUNNEL_SMS_CONSENT_TEXT = "By submitting this form, you agree to receive text messages regarding your quote request, appointment updates, customer service, or account notifications from WindowScreenQuote.com. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance. SMS consent is not shared with third parties or affiliates for marketing purposes.";

function priceRange(total) {
  const low = Math.max(0, Math.floor((total * 0.9) / 10) * 10);
  const high = Math.ceil((total * 1.1) / 10) * 10;
  return { low, high };
}

function newSessionId() {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function PublicEstimate() {
  const params = useParams();
  const navigate = useNavigate();
  // The canonical /quote route has no :slug param → render the synthetic
  // "platform" funnel (leads land in `unassigned_leads`). Per-company funnels
  // come in on /:slug/quote.
  const slug = params.slug || "platform";
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const sessionIdRef = useRef(newSessionId());
  const firedRef = useRef(new Set());

  // Form state
  const [contact, setContact] = useState({ first_name: "", last_name: "", phone: "", email: "", zip_code: "", sms_consent: false });
  const [selectedServices, setSelectedServices] = useState([]); // [{service_key, quantity, mesh_id}]

  const captureMode = company?.settings?.lead_capture_mode || "max";
  const baseStepKeys = STEPS_BY_MODE[captureMode] || STEPS_BY_MODE.max;
  const isPlatformFunnel = slug === "platform";
  const stepKeys = useMemo(
    () => (isPlatformFunnel ? [PLATFORM_ZIP_STEP, ...baseStepKeys] : baseStepKeys),
    [isPlatformFunnel, baseStepKeys],
  );

  const pricingSource = company?.pricing_source || "platform_default";
  const pricingZip = company?.pricing_zip || "";

  const fireEvent = (event_type, metadata = {}) => {
    if (firedRef.current.has(event_type)) return;
    firedRef.current.add(event_type);
    if (!company) return;
    axios.post(`${API}/public/companies/${slug}/events`, {
      event_type,
      session_id: sessionIdRef.current,
      metadata,
    }).catch(() => {});
    // Mirror funnel events to GA4 — names re-mapped to our public catalog.
    const GA_MAP = {
      funnel_started: "quote_started",
      zip_entered: "zip_entered",
      services_selected: "services_selected",
      quantity_set: "quantity_set",
      mesh_selected: "mesh_selected",
      range_viewed: "range_viewed",
      quote_viewed: "quote_viewed",
      contact_submitted: "lead_submitted",
    };
    const gaName = GA_MAP[event_type];
    if (gaName) {
      const params = { ...metadata };
      if (metadata.total != null) {
        params.value = Number(metadata.total);
        params.currency = "USD";
      }
      trackGA(gaName, params);
    }
  };

  useEffect(() => {
    // Capture UTM / referrer / gclid / fbclid on the very first render so we
    // credit the correct ad channel even if the visitor navigates through
    // multiple steps before submitting. Persists to localStorage so a
    // return-visitor's original acquisition source is preserved forever.
    recordAttributionOnMount();
    axios.get(`${API}/public/companies/${slug}`)
      .then(({ data }) => {
        // Historical-slug redirect: if the requested slug is an old one, the
        // API returns the current company with a `canonical_slug` field.
        // Route the browser to the canonical URL so the address bar reflects
        // reality and the correct tenant pixels fire.
        if (data.canonical_slug && data.canonical_slug !== slug) {
          navigate(`/${data.canonical_slug}/quote`, { replace: true });
          return;
        }
        setCompany(data);
        const root = document.documentElement;
        const primary = data.branding?.primary_color || "#0F172A";
        const accent = data.branding?.accent_color || "#0F172A";
        root.style.setProperty("--tenant-primary", primary);
        root.style.setProperty("--tenant-primary-hover", shade(primary, -10));
        root.style.setProperty("--tenant-accent", accent);
        // Install per-tenant FB Pixel + Google Ads tag if the company has
        // configured them. Fires their own PageView automatically. Never runs
        // on the platform funnel (slug === "platform") because the company
        // hasn't been resolved yet at that step.
        if (slug !== "platform") {
          installTenantPixels(data.tracking);
        }
      })
      .catch(() => setError("Company not found"));
  }, [slug, navigate]);

  const refetchForZip = async (zip) => {
    if (slug !== "platform") return;
    try {
      const { data } = await axios.get(`${API}/public/companies/${slug}`, {
        params: zip ? { zip_code: zip } : {},
      });
      setCompany((prev) => ({ ...(prev || {}), ...data }));
    } catch {
      // Silent — fall back to whatever settings we already have.
    }
  };

  useEffect(() => {
    if (company) fireEvent("funnel_started", { mode: captureMode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const estimate = useMemo(() => {
    if (!company) return null;
    return calculateEstimate({ services: selectedServices, companySettings: company.settings });
  }, [company, selectedServices]);

  // Fire quote_viewed once the user reaches a step that exposes the dollar
  // amount (range / summary / contact). Lets the GA conversion funnel report
  // the {value, currency} of the visible quote — works in all 3 capture modes.
  useEffect(() => {
    const k = stepKeys[step];
    if (!k) return;
    if (["range", "summary", "contact"].includes(k) && estimate && estimate.total > 0) {
      fireEvent("quote_viewed", { total: estimate.total });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, estimate]);

  if (error) return <Centered><h1 className="font-display text-2xl font-bold text-slate-900">404 · {error}</h1><p className="text-slate-500 mt-2">Check the URL and try again.</p></Centered>;
  if (!company) return <Centered><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></Centered>;

  const logoFull = company.branding?.logo_url?.startsWith("/api") ? `${BACKEND_URL}${company.branding.logo_url}` : company.branding?.logo_url;
  const totalSteps = stepKeys.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const goNext = () => {
    setStep((s) => Math.min(s + 1, totalSteps - 1));
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  const submitLead = async () => {
    setSubmitting(true);
    try {
      fireEvent("contact_submitted", { mode: captureMode, total: estimate.total });
      const payload = {
        ...contact,
        services: selectedServices,
        estimate_total: estimate.total,
        estimate_breakdown: estimate,
        notes: "",
        session_id: sessionIdRef.current,
        capture_mode: captureMode,
        consent_text: contact.sms_consent ? FUNNEL_SMS_CONSENT_TEXT : "",
        attribution: getFirstAndLastTouch(),
      };
      const res = await axios.post(`${API}/public/companies/${slug}/leads`, payload);
      const leadId = res?.data?.id || null;
      setLeadId(leadId);
      // Fire conversion on the company's OWN pixels (FB Lead + Google Ads
      // conversion) with the homeowner's quote total as the conversion value.
      // No-op if the company hasn't configured tracking or if slug is platform.
      if (slug !== "platform" && company?.tracking) {
        fireTenantLead(company.tracking, { value: Number(estimate.total || 0), currency: "USD" });
      }
      if (captureMode === "high_intent") {
        setSubmitted(true);
      } else if (step < totalSteps - 1) {
        goNext();
      } else {
        setSubmitted(true);
      }
    } catch (e) {
      toast.error("Couldn't submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (submitted) return <SuccessScreen company={company} estimate={estimate} contact={contact} logoFull={logoFull} leadId={leadId} slug={slug} />;

  const currentKey = stepKeys[step];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F8FAFC" }}>
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          {logoFull ? (
            <img src={logoFull} alt={company.business_name} className="h-10 w-10 rounded-lg object-contain bg-slate-50" />
          ) : (
            <div className="h-10 w-10 rounded-lg font-bold text-white flex items-center justify-center" style={{ background: "var(--tenant-primary)" }}>
              {company.business_name[0]}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display font-bold text-slate-900 truncate">{company.business_name}</div>
            <div className="text-xs text-slate-500 truncate" data-testid="public-estimate-subtitle">Instant Quote</div>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-md">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Step {step + 1} of {totalSteps}</span>
              <span className="text-xs font-semibold text-slate-500">{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-300" style={{ width: `${progress}%`, background: "var(--tenant-primary)" }}></div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden fade-slide-enter" key={step}>
            <div className="p-6 sm:p-8">
              {currentKey === "zip" && (
                <StepZip
                  initialZip={contact.zip_code}
                  pricingSource={pricingSource}
                  pricingZip={pricingZip}
                  onNext={async (z) => {
                    setContact((c) => ({ ...c, zip_code: z }));
                    await refetchForZip(z);
                    fireEvent("zip_entered", { zip: z || "skipped" });
                    goNext();
                  }}
                />
              )}
              {currentKey === "services" && <Step1Services company={company} selected={selectedServices} setSelected={(v) => { setSelectedServices(v); fireEvent("services_selected", { count: v.length }); }} onNext={goNext} onBack={isPlatformFunnel ? goBack : null} pricingSource={pricingSource} pricingZip={pricingZip} />}
              {currentKey === "quantity" && <Step2Quantity company={company} selected={selectedServices} setSelected={setSelectedServices} onNext={() => { fireEvent("quantity_set"); goNext(); }} onBack={goBack} />}
              {currentKey === "mesh" && <Step3Mesh company={company} selected={selectedServices} setSelected={setSelectedServices} onNext={() => { fireEvent("mesh_selected"); goNext(); }} onBack={goBack} />}
              {currentKey === "range" && <StepRange company={company} estimate={estimate} onNext={() => { fireEvent("range_viewed", { total: estimate.total }); goNext(); }} onBack={goBack} />}
              {currentKey === "contact" && <StepContact mode={captureMode} contact={contact} setContact={setContact} onSubmit={submitLead} onBack={goBack} submitting={submitting} />}
              {currentKey === "summary" && <StepSummary mode={captureMode} company={company} contact={contact} selected={selectedServices} estimate={estimate} onBack={goBack} onContinue={captureMode === "high_intent" ? goNext : null} onSubmit={captureMode === "max" ? submitLead : null} onView={() => fireEvent("quote_viewed", { total: estimate.total })} submitting={submitting} />}
            </div>
          </div>

          <div className="mt-4 text-center text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure · Takes 60 seconds · No obligation
          </div>
        </div>
      </main>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">{children}</div>;
}

function TenantBtn({ children, ...props }) {
  return (
    <button
      {...props}
      className={`tenant-btn w-full h-12 px-5 font-semibold text-base flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${props.className || ""}`}
    >
      {children}
    </button>
  );
}

function StepHeader({ kicker, title, subtitle }) {
  return (
    <div className="mb-5">
      {kicker && <div className="text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: "var(--tenant-primary)" }}>{kicker}</div>}
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-tight">{title}</h2>
      {subtitle && <p className="text-slate-500 mt-2 text-sm">{subtitle}</p>}
    </div>
  );
}

function Step1Services({ company, selected, setSelected, onNext, onBack, pricingSource, pricingZip }) {
  const services = company.settings.services || {};
  const isSelected = (k) => selected.some((s) => s.service_key === k);
  const toggle = (k) => {
    if (isSelected(k)) {
      setSelected(selected.filter((s) => s.service_key !== k));
    } else {
      setSelected([...selected, { service_key: k, quantity: 1, mesh_id: null }]);
    }
  };
  return (
    <div>
      <StepHeader kicker="What do you need?" title="Pick your services" />
      <div className="mb-3 -mt-1 flex flex-wrap items-center gap-2">
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
          style={{ background: "color-mix(in srgb, var(--tenant-primary) 10%, transparent)", color: "var(--tenant-primary)" }}
        >
          <Check className="w-3 h-3" /> Tap all that apply — multi-select OK
        </div>
        {pricingSource === "local_company" && pricingZip && (
          <LocalPricingPill zip={pricingZip} />
        )}
      </div>
      <div className="space-y-2.5">
        {Object.entries(services).filter(([, v]) => v.enabled).map(([k]) => {
          const sel = isSelected(k);
          return (
            <button
              key={k}
              onClick={() => toggle(k)}
              data-testid={`funnel-service-${k}`}
              className={`tenant-chip w-full text-left p-4 flex items-center gap-3 ${sel ? "selected" : ""}`}
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-50 shrink-0">
                {SERVICE_META[k]?.Icon && (() => { const C = SERVICE_META[k].Icon; return <C />; })()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">{SERVICE_META[k]?.label || serviceLabel(k)}</div>
                <div className="text-xs text-slate-500">{SERVICE_META[k]?.desc}</div>
              </div>
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${sel ? "" : "border-slate-300"}`} style={sel ? { background: "var(--tenant-primary)", borderColor: "var(--tenant-primary)" } : {}}>
                {sel && <Check className="w-3.5 h-3.5 text-white" />}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-6 flex gap-2">
        {onBack && (
          <Button variant="outline" onClick={onBack} className="rounded-xl h-12 flex-1" data-testid="funnel-step1-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        )}
        <div className={onBack ? "flex-[2]" : "w-full"}>
          <TenantBtn onClick={onNext} disabled={selected.length === 0} data-testid="funnel-step1-next">Continue <ArrowRight className="w-4 h-4" /></TenantBtn>
        </div>
      </div>
    </div>
  );
}

function LocalPricingPill({ zip }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest bg-emerald-100 text-emerald-800"
      title={`Pricing tuned for ${zip}`}
      data-testid="funnel-local-pricing-pill"
    >
      <Check className="w-3 h-3" /> Local pricing · {zip}
    </span>
  );
}

function StepZip({ initialZip, pricingSource, pricingZip, onNext }) {
  const [zip, setZip] = useState(initialZip || "");
  const [loading, setLoading] = useState(false);
  const valid = /^\d{5}$/.test(zip);

  const submit = async () => {
    setLoading(true);
    try { await onNext(zip); } finally { setLoading(false); }
  };

  return (
    <div>
      <StepHeader
        kicker="Step 1"
        title="Where's the job?"
        subtitle="We'll show you prices from a real local screen pro for your area."
      />
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Zip code</Label>
        <Input
          autoFocus
          inputMode="numeric"
          maxLength={5}
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
          onKeyDown={(e) => { if (e.key === "Enter" && valid) submit(); }}
          placeholder="e.g. 85001"
          className="h-12 rounded-xl mt-1.5 text-lg tracking-widest font-semibold"
          data-testid="funnel-step-zip-input"
        />
        {pricingSource === "local_company" && pricingZip && pricingZip !== zip && (
          <p className="text-[11px] text-emerald-700 mt-2 font-semibold">Local pricing was loaded for {pricingZip}. Update the zip if needed.</p>
        )}
      </div>
      <div className="mt-6 space-y-2">
        <TenantBtn onClick={submit} disabled={!valid || loading} data-testid="funnel-step-zip-next">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking your area…</> : <>See my price <ArrowRight className="w-4 h-4" /></>}
        </TenantBtn>
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed pt-5">
        We use your zip only to surface the pricing of a local screen company that serves your area.
      </p>
    </div>
  );
}

function Step2Quantity({ company, selected, setSelected, onNext, onBack }) {
  const updateSvc = (key, patch) => setSelected(selected.map((s) => s.service_key === key ? { ...s, ...patch } : s));
  const setQty = (key, q) => {
    const qty = Math.max(1, Math.min(99, q));
    updateSvc(key, { quantity: qty });
  };

  return (
    <div>
      <StepHeader kicker="Step 2" title="How many?" subtitle="Tap + or − to adjust the quantity for each service." />
      <div className="space-y-3">
        {selected.map((svc) => (
          <div key={svc.service_key} className="rounded-2xl border border-slate-200 p-4">
            {/* Full-width header row — no truncation */}
            <div className="flex items-start gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-50 shrink-0">
                {SERVICE_META[svc.service_key]?.Icon && (() => { const C = SERVICE_META[svc.service_key].Icon; return <C />; })()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-base leading-tight">{SERVICE_META[svc.service_key]?.label || serviceLabel(svc.service_key)}</div>
                <div className="text-xs text-slate-500 mt-0.5 leading-snug">{SERVICE_META[svc.service_key]?.desc}</div>
              </div>
            </div>
            {/* Qty stepper on its own row */}
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Quantity</div>
              <div className="flex items-center gap-2">
                <button onClick={() => setQty(svc.service_key, svc.quantity - 1)} className="w-10 h-10 rounded-lg border border-slate-200 hover:border-slate-300 flex items-center justify-center" data-testid={`funnel-qty-minus-${svc.service_key}`}><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center font-bold text-slate-900 text-lg" data-testid={`funnel-qty-${svc.service_key}`}>{svc.quantity}</span>
                <button onClick={() => setQty(svc.service_key, svc.quantity + 1)} className="w-10 h-10 rounded-lg border border-slate-200 hover:border-slate-300 flex items-center justify-center" data-testid={`funnel-qty-plus-${svc.service_key}`}><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-2">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-12 flex-1"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex-[2]"><TenantBtn onClick={onNext} data-testid="funnel-step2-next">Continue <ArrowRight className="w-4 h-4" /></TenantBtn></div>
      </div>
    </div>
  );
}

function Step3Mesh({ company, selected, setSelected, onNext, onBack }) {
  const allMesh = (company.settings.mesh_upgrades || []).filter((m) => m.enabled);

  const meshForService = (serviceKey) => {
    const isDoor = serviceKey === "sliding_door_rescreen" || serviceKey === "new_sliding_door";
    return allMesh.filter((m) => m.is_default || (isDoor ? m.show_on_doors !== false : m.show_on_windows !== false));
  };

  const setMeshForService = (svcKey, meshId) => {
    setSelected(selected.map((s) => s.service_key === svcKey ? { ...s, mesh_id: meshId } : s));
  };

  return (
    <div>
      <StepHeader kicker="Step 3" title="Upgrade your mesh?" subtitle="All quotes include high-quality standard fiberglass. Upgrade any service below." />
      <div className="space-y-5">
        {selected.map((svc) => {
          const mesh = meshForService(svc.service_key);
          const defaultMesh = mesh.find((m) => m.is_default);
          const current = svc.mesh_id || defaultMesh?.id;
          return (
            <div key={svc.service_key} className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{SERVICE_META[svc.service_key]?.label} · {svc.quantity} screen{svc.quantity > 1 ? "s" : ""}</div>
              <div className="space-y-2">
                {mesh.map((m) => {
                  const isSel = current === m.id;
                  const pricePerScreen = meshPriceForService(m, svc.service_key);
                  const showImage = !!company.settings?.show_mesh_images && !!m.image_url;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setMeshForService(svc.service_key, m.id)}
                      data-testid={`funnel-mesh-${svc.service_key}-${m.id}`}
                      className={`tenant-chip w-full text-left p-3 flex items-center gap-3 ${isSel ? "selected" : ""}`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${isSel ? "" : "border-slate-300"}`} style={isSel ? { borderColor: "var(--tenant-primary)" } : {}}>
                        {isSel && <div className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--tenant-primary)" }}></div>}
                      </div>
                      {showImage && (
                        <div className="w-14 h-14 rounded-xl shrink-0 overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                          <img src={m.image_url.startsWith("/api") ? `${BACKEND_URL}${m.image_url}` : m.image_url} alt={m.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{m.name}</span>
                          {!m.is_default && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Upgrade</span>}
                          {m.is_default && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">Included</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{m.description}</div>
                      </div>
                      <div className="text-right shrink-0">
                        {m.is_default || pricePerScreen === 0 ? (
                          <div className="text-xs text-emerald-600 font-bold">Free</div>
                        ) : (
                          <div>
                            <div className="text-sm font-bold text-slate-900">+${pricePerScreen.toFixed(0)}<span className="text-[10px] font-bold text-slate-400">/screen</span></div>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 flex gap-2">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-12 flex-1"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex-[2]"><TenantBtn onClick={onNext} data-testid="funnel-step3-next">Continue <ArrowRight className="w-4 h-4" /></TenantBtn></div>
      </div>
    </div>
  );
}

function StepContact({ mode, contact, setContact, onSubmit, onBack, submitting }) {
  const set = (k) => (e) => setContact({ ...contact, [k]: e.target.value });
  const canSubmit = contact.first_name && contact.email && contact.phone && contact.zip_code;

  const titles = {
    max: { kicker: "Almost there", title: "Where do we send your quote?", subtitle: "Enter your info to see your price.", cta: "See my quote" },
    high_intent: { kicker: "Like what you see?", title: "Send my request", subtitle: "We'll connect you with our team to schedule.", cta: "Send my request" },
    hybrid: { kicker: "Unlock your exact quote", title: "One last step", subtitle: "Enter your info to see the exact quote and save your selections.", cta: "Unlock my quote" },
  };
  const t = titles[mode] || titles.max;

  return (
    <div>
      <StepHeader kicker={t.kicker} title={t.title} subtitle={t.subtitle} />
      <div className="space-y-3.5">
        <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">First name</Label>
          <Input className="h-12 rounded-xl mt-1.5" value={contact.first_name} onChange={set("first_name")} data-testid="funnel-first-name" /></div>
        <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Phone</Label>
          <Input type="tel" className="h-12 rounded-xl mt-1.5" placeholder="(555) 555-1234" value={contact.phone} onChange={set("phone")} data-testid="funnel-phone" /></div>
        <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
          <Input type="email" className="h-12 rounded-xl mt-1.5" value={contact.email} onChange={set("email")} data-testid="funnel-email" /></div>
        <div><Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Zip code</Label>
          <Input className="h-12 rounded-xl mt-1.5" inputMode="numeric" value={contact.zip_code} onChange={set("zip_code")} data-testid="funnel-zip" /></div>
        <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
          <Checkbox checked={contact.sms_consent} onCheckedChange={(v) => setContact({ ...contact, sms_consent: !!v })} className="mt-0.5" data-testid="funnel-sms-consent" />
          <span className="text-xs text-slate-600 leading-relaxed">
            By checking this box, you agree to receive text messages from WindowScreenQuote.com regarding your quote request, appointment updates, customer service, or account notifications. Message frequency varies. Message and data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance. SMS consent is not shared with third parties or affiliates for marketing purposes. See our{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">Privacy Policy</a>{" "}and{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">Terms</a>.
          </span>
        </label>
      </div>
      <div className="mt-6 flex gap-2">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-12 flex-1" disabled={submitting}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex-[2]">
          <TenantBtn onClick={onSubmit} disabled={!canSubmit || submitting} data-testid="funnel-step-contact-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>{t.cta} <ArrowRight className="w-4 h-4" /></>}
          </TenantBtn>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed pt-4">
        By submitting this quote request you agree to our{" "}
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">Terms</a>{" "}
        and{" "}
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">Privacy Policy</a>, and agree that the local screen company may contact you about your quote at the email address and phone number you provided.
      </p>
    </div>
  );
}

function StepRange({ company, estimate, onNext, onBack }) {
  const { low, high } = priceRange(estimate.total);
  return (
    <div>
      <StepHeader kicker="Your estimated price range" title={`$${low} - $${high}`} subtitle="Based on your selections so far." />
      <div className="rounded-2xl bg-slate-50 p-5 mb-5 text-center">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Estimated range</div>
        <div className="font-display text-4xl font-bold mt-1" style={{ color: "var(--tenant-primary)" }} data-testid="funnel-range">${low} - ${high}</div>
        <div className="text-xs text-slate-500 mt-2">Most homeowners with similar selections land in this range.</div>
      </div>
      <div className="rounded-2xl border-2 border-dashed p-4 mb-5 text-center" style={{ borderColor: "color-mix(in srgb, var(--tenant-primary) 30%, transparent)" }}>
        <Lock className="w-5 h-5 mx-auto mb-2" style={{ color: "var(--tenant-primary)" }} />
        <div className="text-sm font-semibold text-slate-900">Unlock your exact quote</div>
        <div className="text-xs text-slate-500 mt-1">See the itemized price and save your selections.</div>
      </div>
      <div className="text-[11px] text-slate-400 leading-relaxed mb-5">
        Final price may vary slightly based on measurements, screen condition, or special requests.
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-12 flex-1"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex-[2]"><TenantBtn onClick={onNext} data-testid="funnel-step-range-next">Unlock my quote <ArrowRight className="w-4 h-4" /></TenantBtn></div>
      </div>
    </div>
  );
}

function StepSummary({ mode, company, contact, selected, estimate, onBack, onSubmit, onContinue, onView, submitting }) {
  useEffect(() => { if (onView) onView(); }, [onView]);
  const greeting = contact.first_name ? `Hi ${contact.first_name}, here's your price` : "Here's your instant quote";
  const subtitle = mode === "high_intent"
    ? "Like what you see? Send your request and your local screen pro will reach out."
    : "Your quote is locked in — your local screen pro will reach out to schedule.";
  return (
    <div>
      <StepHeader kicker="Your instant quote" title={greeting} subtitle={subtitle} />

      <div className="rounded-2xl bg-slate-50 p-5 mb-5">
        <div className="space-y-2.5">
          {estimate.line_items.map((li, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-slate-700">{li.label} <span className="text-slate-400">× {li.qty}</span></span>
              <span className="font-semibold text-slate-900">${li.amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-1.5 text-sm">
          {estimate.discount > 0 && (
            <div className="flex justify-between"><span className="text-slate-600">Bulk discount ({estimate.discount_percent}%)</span><span className="text-emerald-600 font-semibold">−${estimate.discount.toFixed(2)}</span></div>
          )}
          {estimate.minimum_applied > 0 && (
            <div className="flex justify-between text-xs"><span className="text-slate-500">Minimum service applied</span><span className="text-slate-500">+${estimate.minimum_applied.toFixed(2)}</span></div>
          )}
          {estimate.tax > 0 && (
            <div className="flex justify-between"><span className="text-slate-600">Tax</span><span>${estimate.tax.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between text-2xl pt-3 items-baseline">
            <span className="font-display font-bold text-slate-900">Total</span>
            <span className="font-display font-bold" style={{ color: "var(--tenant-primary)" }} data-testid="funnel-total">${estimate.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-5">
        <div className="flex items-start gap-2.5 text-sm text-slate-700">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tenant-primary)" }} />
          <span>Includes professional installation and our quality guarantee.</span>
        </div>
        <div className="flex items-start gap-2.5 text-sm text-slate-700">
          <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
          <span>No deposit. You only pay after the job's done.</span>
        </div>
        <div className="text-[11px] text-slate-400 leading-relaxed pt-2">
          Your instant quote is based on the information provided and may vary slightly based on measurements, screen condition, or special requests.
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-12 flex-1" disabled={submitting}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="flex-[2]">
          {onSubmit && (
            <TenantBtn onClick={onSubmit} disabled={submitting} data-testid="funnel-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Get my quote <ArrowRight className="w-4 h-4" /></>}
            </TenantBtn>
          )}
          {onContinue && (
            <TenantBtn onClick={onContinue} disabled={submitting} data-testid="funnel-continue-btn">
              Request service <ArrowRight className="w-4 h-4" />
            </TenantBtn>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessScreen({ company, estimate, contact, logoFull, leadId, slug }) {
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const PLATFORM = slug === "platform";

  // Fire a fresh PageView on the tenant's FB Pixel so their conversion
  // funnel shows the thank-you view as its own event (some setups configure
  // PageView-on-thank-you as their goal instead of Lead). Idempotent.
  useEffect(() => {
    if (!PLATFORM && company?.tracking) fireTenantPageView(company.tracking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click-to-text: only enabled for tenant funnels where the company has
  // published a phone number in Settings. Falls back to the internal
  // "Ready to Book" button when no phone is on file so the CTA is never dead.
  const rawPhone = (company?.business_info?.phone || "").replace(/[^0-9+]/g, "");
  const canText = !PLATFORM && rawPhone.length >= 10;
  const smsBody = canText
    ? `Hi ${company.business_name} — this is ${contact.first_name}. I just got a quote for $${Number(estimate.total || 0).toFixed(2)} through your site. Ready to book — when can we schedule?`
    : "";
  const smsHref = canText ? `sms:${rawPhone}?&body=${encodeURIComponent(smsBody)}` : null;

  const bookNow = async () => {
    if (!leadId) return;
    setBooking(true);
    try {
      await axios.post(`${API}/public/leads/${leadId}/ready-to-book`);
      trackGA("ready_to_book_clicked", { value: Number(estimate.total || 0), currency: "USD" });
      setBooked(true);
    } catch (_e) {
      toast.error("Couldn't submit — please try again.");
    } finally {
      setBooking(false);
    }
  };

  // Also called when the customer taps the primary Text CTA — we still want
  // to flag the lead as "ready to book" internally so the company sees the
  // status change even if the customer never actually sends the SMS.
  const markReadyThenText = () => {
    trackGA("ready_to_book_text_tapped", { value: Number(estimate.total || 0), currency: "USD" });
    // Fire-and-forget — don't block the sms: link from opening if this errors.
    if (leadId) {
      axios.post(`${API}/public/leads/${leadId}/ready-to-book`).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#F8FAFC" }}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden text-center" data-testid="funnel-success">
        <div className="px-8 pt-10 pb-6">
          <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ background: "color-mix(in srgb, var(--tenant-primary) 12%, transparent)" }}>
            <Check className="w-8 h-8" style={{ color: "var(--tenant-primary)" }} />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">You're all set, {contact.first_name}!</h1>
          <p className="text-slate-500 mt-2 text-sm">
            {PLATFORM
              ? <>We'll match you with a local screen pro and they'll reach out to confirm and schedule.</>
              : <>Here's your quote from <strong>{company.business_name}</strong>. Tap below to book — they'll be ready for your text.</>}
          </p>
        </div>
        <div className="mx-6 mb-5 rounded-2xl bg-slate-50 p-5 text-left">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold text-center">Your quote</div>
          <div className="font-display text-4xl font-bold mt-1 text-center" style={{ color: "var(--tenant-primary)" }}>${estimate.total.toFixed(2)}</div>

          {estimate.line_items && estimate.line_items.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-1.5">
              {estimate.line_items.map((li, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-600 truncate pr-3">{li.label} <span className="text-slate-400">× {li.qty}</span></span>
                  <span className="font-semibold text-slate-900 shrink-0">${li.amount.toFixed(2)}</span>
                </div>
              ))}
              {estimate.discount > 0 && (
                <div className="flex justify-between text-sm pt-1.5 border-t border-slate-100 mt-1.5">
                  <span className="text-slate-600">Bulk discount ({estimate.discount_percent}%)</span>
                  <span className="text-emerald-600 font-semibold">−${estimate.discount.toFixed(2)}</span>
                </div>
              )}
              {estimate.minimum_applied > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Minimum service applied</span>
                  <span className="text-slate-500">+${estimate.minimum_applied.toFixed(2)}</span>
                </div>
              )}
              {estimate.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Tax</span>
                  <span className="text-slate-900">${estimate.tax.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm pt-2 border-t border-slate-200 mt-2">
                <span className="font-bold text-slate-900">Total</span>
                <span className="font-bold text-slate-900">${estimate.total.toFixed(2)}</span>
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500 mt-4 text-center">Confirmation sent to {contact.email}</div>
        </div>

        {/* ─── PRIMARY CTA ────────────────────────────────────────────────
             For tenants w/ a phone on file: "Ready to book?" headline + big
             click-to-text button. For the platform funnel (or tenants
             missing a phone), fall back to the internal "I'm Ready to Book"
             flow so the CTA is never dead. */}
        {!booked && canText ? (
          <div className="px-6 pb-2">
            <div className="text-lg font-display font-bold text-slate-900 mb-2" data-testid="funnel-ready-to-book-headline">Ready to book?</div>
            <a
              href={smsHref}
              onClick={markReadyThenText}
              data-testid="funnel-text-cta"
              className="w-full rounded-2xl h-14 text-base font-bold shadow-lg inline-flex items-center justify-center gap-2 text-white transition-transform active:scale-[0.98]"
              style={{ background: "var(--tenant-primary)" }}
            >
              <MessageSquare className="w-5 h-5" /> Text {company.business_name} now
            </a>
            <div className="text-xs text-slate-500 mt-2.5 leading-relaxed">
              Opens your Messages app with your quote pre-filled. {company.business_name} texts you back to lock in a time.
            </div>
            {/* Secondary — silent-notify escape hatch */}
            <button
              type="button"
              onClick={bookNow}
              disabled={booking || !leadId}
              data-testid="funnel-notify-instead"
              className="mt-4 text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2 inline-flex items-center gap-1 disabled:opacity-50"
            >
              {booking ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : <>Or notify {company.business_name} without texting</>}
            </button>
            {company.business_info?.phone && (
              <div className="text-xs text-slate-400 mt-2 flex items-center justify-center gap-1">
                <PhoneIcon className="w-3 h-3" /> Or call <a href={`tel:${rawPhone}`} className="font-semibold hover:text-slate-900">{company.business_info.phone}</a>
              </div>
            )}
          </div>
        ) : !booked ? (
          // Fallback — platform funnel OR tenant without a phone number
          <div className="px-6 pb-5">
            <Button
              onClick={bookNow}
              disabled={booking || !leadId}
              size="lg"
              className="w-full rounded-2xl h-14 text-base font-bold shadow-lg"
              style={{ background: "var(--tenant-primary)" }}
              data-testid="funnel-ready-to-book"
            >
              {booking ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <>I'm Ready To Book My Appointment <ArrowRight className="w-5 h-5 ml-1.5" /></>}
            </Button>
            <div className="text-xs text-slate-500 mt-3 leading-relaxed">
              Tap to let {PLATFORM ? "a local screen pro" : company.business_name} know you'd like to set an appointment. They'll be notified right away.
            </div>
          </div>
        ) : (
          <div className="px-6 pb-5" data-testid="funnel-booked-state">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-left">
              <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm">
                <Check className="w-4 h-4" /> Sent
              </div>
              <div className="text-sm text-emerald-900 mt-1 leading-relaxed">
                {PLATFORM ? "A local screen pro" : company.business_name} has been notified you're ready to book. Expect a call or text shortly.
              </div>
            </div>
          </div>
        )}

        <div className="px-6 pb-3 pt-3 text-[11px] text-slate-400 leading-relaxed">
          Your instant quote is based on the information provided and may vary slightly based on measurements, screen condition, or special requests.
        </div>
        {company.business_info?.phone && !PLATFORM && !canText && (
          <div className="px-6 pb-8 text-sm text-slate-600">
            Questions? Call us at <a href={`tel:${company.business_info.phone}`} className="font-bold" style={{ color: "var(--tenant-primary)" }}>{company.business_info.phone}</a>
          </div>
        )}
      </div>
    </div>
  );
}

function shade(hex, percent) {
  try {
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    let r = parseInt(h.substring(0, 2), 16);
    let g = parseInt(h.substring(2, 4), 16);
    let b = parseInt(h.substring(4, 6), 16);
    r = Math.max(0, Math.min(255, r + Math.round((r * percent) / 100)));
    g = Math.max(0, Math.min(255, g + Math.round((g * percent) / 100)));
    b = Math.max(0, Math.min(255, b + Math.round((b * percent) / 100)));
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
  } catch { return hex; }
}
